import React, { ReactNode, useContext, useEffect, useState } from "react";
import IconClose from "../Icon/IconClose";
import { useIsDesktop } from "lib/device";
import { isMoodleURL, useIsSubdomain, useIsTenant } from "lib/host";
import { motion } from "framer-motion";
import { useModalStore } from "./modalStore";
import { useSystemStore } from "components/PageHeader/systemStore";

const useOutsideClick = (callback: () => void) => {
  const ref = React.useRef();

  React.useEffect(() => {
    const handleClick = (event: any) => {
      if (ref.current && !(ref.current as any)?.contains(event.target)) {
        callback();
      }
    };

    document.addEventListener("click", handleClick, true);

    return () => {
      document.removeEventListener("click", handleClick, true);
    };
  }, [ref]);

  return ref;
};

export type Props = {
  useParentRef?: any;
  isAlert?: boolean;
  isQuiz?: boolean;
  isShow?: boolean;
  isSearch?: boolean;
  isFullScreen?: boolean;
  isTopBorderTransparent?: boolean;
  contentStyles?: object;
  contentWidth?: number;
  isWidthScreen?: string;
  className?: string;
  childrenClassName?: string;
  contentClassName?: string;
  zIndex?: number;
  title?: string | ReactNode;
  onClose?(): void;
  onClickOutside?(): void;
  children: ReactNode;
  color?: string;
  hFull?: Boolean;
  bottomChildren?: ReactNode;
  customTitleElement?: ReactNode;
  isBundles?: boolean;
  mainContentHeight?: number;
};

const Modal: React.FC<Props> = ({
  useParentRef = null,
  isAlert = false,
  isQuiz = false,
  isShow,
  isSearch = false,
  isFullScreen,
  isTopBorderTransparent,
  className,
  childrenClassName,
  contentClassName,
  zIndex,
  contentStyles,
  contentWidth,
  isWidthScreen,
  title,
  onClose,
  onClickOutside,
  children,
  color = "#19093A",
  hFull = true,
  bottomChildren,
  customTitleElement,
  isBundles = false,
  mainContentHeight,
}) => {
  const isDesktop = useIsDesktop();
  const isSubdomainTenant = useIsSubdomain();
  const isTenantURL = useIsTenant();
  const isB2B = isTenantURL;
  const [isClosed, setisClosed] = useState(false);
  // const { openModal } = useModalStore((state) => state);
  const isSijuleTenant =
    typeof window !== "undefined" && isMoodleURL(window.location.hostname);
  const theme = useSystemStore((state) => state.theme);
  const screen = typeof window !== "undefined" && window.innerWidth;
  const isMobile = screen < 770;

  const transitionDuration = 700;

  const onCloseModal = () => {
    // openModal(false);
    setisClosed(true);
    setTimeout(() => {
      onClose();
      setisClosed(false);
    }, transitionDuration);
  };

  const ref = useOutsideClick(() => {
    if (onClickOutside) {
      setisClosed(true);
      setTimeout(() => {
        onClickOutside();
        setisClosed(false);
      }, transitionDuration);
    }
  });

  // if (isSearch) {
  //   openModal(isSearch);
  // }

  const [heightContent, setHeightContent] = useState(0);

  useEffect(() => {
    if (mainContentHeight) {
      setHeightContent(mainContentHeight);
    }
  }, [mainContentHeight, isMobile]);

  // console.log("mainContentHeight", mainContentHeight);
  // console.log("heightContent", heightContent);

  if (isShow) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={isClosed ? { opacity: 0 } : { opacity: 1 }}
        style={{ zIndex: zIndex || 9999, position: "relative" }}
        transition={{ duration: transitionDuration / 1000 }}
      >
        <div
          className={`fixed inset-0 flex ${
            !isFullScreen && isSijuleTenant
              ? "sg-max:items-start"
              : "sg-max:items-end"
          } ${isB2B ? "items-start" : "items-center"} justify-center ${
            !isBundles
              ? "bg-neutral80_to_dark00"
              : `${isMobile ? "pointer-events-none" : "bg-neutral80_to_dark00"}`
          } ${className || ""}`}
          style={{ zIndex: zIndex || 9999 }}
        >
          <div
            ref={ref}
            className={`relative ${
              isAlert && !isMobile ? "w-[50%]" : "w-full"
            } bg-slated_grey2 md:max-w-[740px] max-w-screen overflow-auto
              ${
                !isBundles
                  ? "px-16 py-16 md:rounded-12 rounded-t-12"
                  : `${
                      isMobile
                        ? `pointer-events-auto h-[calc(100vh-${heightContent}px)]`
                        : "md:rounded-12 rounded-t-12"
                    }`
              }
              ${contentClassName || ""}`}
            style={{
              maxHeight:
                !isFullScreen && isSijuleTenant
                  ? "74%"
                  : isFullScreen && isSijuleTenant
                  ? "85%"
                  : isFullScreen && !isSijuleTenant
                  ? "95%"
                  : "90%",
              maxWidth: isWidthScreen || contentWidth,
              marginTop:
                isQuiz && !isFullScreen && isSijuleTenant
                  ? "110px"
                  : isQuiz && isFullScreen && isSijuleTenant
                  ? "40px"
                  : "0px",
              ...contentStyles,
            }}
          >
            {customTitleElement ?? (
              <div
                className={`flex items-center ${
                  isMobile && isBundles ? "z-10 sticky top-0 bg-white" : ""
                } ${
                  isFullScreen
                    ? " border-b border-neutral20_to_dark30 px-16 py-16"
                    : ""
                } ${isTopBorderTransparent ? " !border-transparent" : ""}`}
              >
                {title && (
                  <h3 className="text-b1 text-neutral-80 font-bold">{title}</h3>
                )}
                {onClose && (
                  <button
                    type="button"
                    className="ml-auto"
                    onClick={onCloseModal}
                  >
                    <IconClose
                      size={20}
                      color={theme == "dark" ? "#CCCCCC" : "#3E3851"}
                    />
                  </button>
                )}
              </div>
            )}

            <div className={childrenClassName}>{children}</div>

            {bottomChildren && (
              <div
                className={`flex items-center ${
                  isFullScreen ? "border-t border-neutral20_to_dark30" : ""
                }`}
              >
                {bottomChildren}
              </div>
            )}
          </div>
        </div>

        <style>{`
          html, body {
            overflow: hidden;
          }
        `}</style>
      </motion.div>
    );
  }
  return null;
};

export default Modal;
